import { z } from 'zod';
import { parseEurCents } from './cashFlow';

/**
 * Bank-balance anchor client. The primary view's running balance rolls FORWARD
 * from the OPENING anchor — the earliest-dated entry (a one-time "aasta alguse
 * seis"; same-date corrections resolve to the newest). Later observations (e.g.
 * LHV statement imports) never move the opening — they surface only as the
 * informational "Pangajääk" chip. This boundary Zod-validates every payload (anti-slop
 * rule) and carries no money maths — the entered cents ride through verbatim
 * (L9); parsing the typed € string is `parseEurCents`' job (shared, golden-
 * tested), with only the SIGN handled here (an overdrawn account is a real
 * observation the shared positive-amount parser rejects on purpose).
 */

export const bankAnchorSchema = z.object({
  id: z.string(),
  balanceCents: z.number().int(),
  asOfDate: z.string(),
  source: z.enum(['manual', 'lhv']),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type BankAnchor = z.infer<typeof bankAnchorSchema>;

export const bankAnchorsResponseSchema = z.object({
  today: z.string(),
  /** The OPENING anchor the balance rolls forward from (earliest ≤ today) or null. */
  current: bankAnchorSchema.nullable(),
  /** Recent history, newest first (append-only — a correction is a newer row). */
  recent: z.array(bankAnchorSchema),
});
export type BankAnchorsResponse = z.infer<typeof bankAnchorsResponseSchema>;

export interface SetBankAnchorInput {
  balanceCents: number;
  asOfDate?: string;
  note?: string;
}

export interface AnchorRequestOptions {
  /** Injectable fetch (tests). Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /** Base URL; defaults to same-origin (relative) so it works everywhere. */
  baseUrl?: string;
  /** Supabase access token; attached as a Bearer header when present. */
  accessToken?: string;
}

function authHeaders(
  accessToken: string | undefined,
  base: Record<string, string> = {},
): Record<string, string> {
  return accessToken ? { ...base, Authorization: `Bearer ${accessToken}` } : base;
}

/** Read the current anchor + recent history for the Seaded panel. */
export async function fetchBankAnchors(
  options: AnchorRequestOptions = {},
): Promise<BankAnchorsResponse> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/anchor`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Anchor request failed: ${res.status}`);
  }
  return bankAnchorsResponseSchema.parse(await res.json());
}

/** Append a manual bank-balance observation; returns the stored anchor. */
export async function setBankAnchor(
  input: SetBankAnchorInput,
  options: AnchorRequestOptions = {},
): Promise<BankAnchor> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/anchor`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Anchor save failed: ${res.status}`);
  }
  const parsed = z.object({ anchor: bankAnchorSchema }).parse(await res.json());
  return parsed.anchor;
}

/**
 * Parse a typed € amount that MAY carry a leading minus (an overdrawn account).
 * Delegates the magnitude to the shared `parseEurCents` (which rejects signs by
 * design — payment amounts are always positive) and applies the sign here.
 * Returns integer cents or null when the input is not a valid amount.
 */
export function parseSignedEurCents(raw: string): number | null {
  const trimmed = raw.trim();
  // Accept both the ASCII hyphen and the typographic minus the UI renders.
  const negative = trimmed.startsWith('-') || trimmed.startsWith('−');
  const magnitude = parseEurCents(negative ? trimmed.slice(1) : trimmed);
  if (magnitude === null) return null;
  return negative ? -magnitude : magnitude;
}
